import PropTypes from 'prop-types';
import {
  getSimpleAppearanceValue,
  isSimpleSectionNode,
  SIMPLE_APPEARANCE_OPTIONS,
} from '../../utilities/simpleEditorMode.js';

const TEXT_TAGS = new Set(['a', 'blockquote', 'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'p']);

const GuidedChoiceGroup = ({
  controlId,
  nodeId,
  label,
  options,
  value,
  onChange,
}) => (
  <fieldset className="mb-3">
    <legend className="form-label small fw-semibold mb-2">{label}</legend>
    <div className="simple-choice-grid guided-choice-grid">
      {options.map((option, index) => {
        const optionId = `simple-${controlId}-${nodeId}-${index}`;
        const selected = value === option.value;
        return (
          <div className="guided-choice" key={option.value || 'default'}>
            <input
              className="guided-choice-input"
              id={optionId}
              name={`simple-${controlId}-${nodeId}`}
              type="radio"
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
            />
            <label className="guided-choice-label" htmlFor={optionId}>
              <span
                className={`guided-choice-visual ${
                  option.previewClass ? `guided-tone-${option.previewClass}` : ''
                }`}
                aria-hidden="true"
              >
                {option.iconClass ? <i className={`bi ${option.iconClass}`}></i> : null}
              </span>
              <span className="guided-choice-copy">
                <strong>{option.label}</strong>
                <small>{option.description}</small>
              </span>
              <i
                className={`bi ${selected ? 'bi-check-circle-fill' : 'bi-circle'} guided-choice-status`}
                aria-hidden="true"
              ></i>
            </label>
          </div>
        );
      })}
    </div>
  </fieldset>
);

GuidedChoiceGroup.propTypes = {
  controlId: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    iconClass: PropTypes.string,
    previewClass: PropTypes.string,
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ToggleChoice = ({
  id,
  label,
  description,
  iconClass,
  value,
  active,
  onChange,
}) => (
  <div className="guided-choice">
    <input
      className="guided-choice-input"
      id={id}
      type="checkbox"
      checked={active}
      onChange={() => onChange(active ? '' : value)}
    />
    <label className="guided-choice-label" htmlFor={id}>
      <span className="guided-choice-visual" aria-hidden="true">
        <i className={`bi ${iconClass}`}></i>
      </span>
      <span className="guided-choice-copy">
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <i
        className={`bi ${active ? 'bi-check-circle-fill' : 'bi-circle'} guided-choice-status`}
        aria-hidden="true"
      ></i>
    </label>
  </div>
);

ToggleChoice.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SimpleElementSettings = ({
  node,
  currentText,
  onTextChange,
  onSetAttribute,
  onRemoveAttribute,
  onChangeImage,
  onApplyAppearance,
}) => {
  const section = isSimpleSectionNode(node);
  const isImage = node.type === 'img';
  const isLink = node.type === 'a';
  const classes = node.attributes?.class || [];
  const newTab = node.attributes?.target === '_blank';
  const canEditText = TEXT_TAGS.has(node.type);

  return (
    <div className="simple-element-settings">
      {canEditText ? (
        <>
          <label className="form-label small fw-semibold" htmlFor={`simple-wording-${node.id}`}>Wording</label>
          <textarea
            id={`simple-wording-${node.id}`}
            className="form-control element-text-content-input mb-3"
            rows="5"
            value={currentText}
            onChange={(event) => onTextChange(event.target.value)}
          />
          <GuidedChoiceGroup
            controlId="emphasis"
            nodeId={node.id}
            label="Emphasis"
            options={SIMPLE_APPEARANCE_OPTIONS.emphasis}
            value={getSimpleAppearanceValue(node, 'emphasis')}
            onChange={(value) => onApplyAppearance('emphasis', value)}
          />
        </>
      ) : null}

      {isLink ? (
        <>
          <label className="form-label small fw-semibold" htmlFor={`simple-link-${node.id}`}>Link destination</label>
          <input
            id={`simple-link-${node.id}`}
            className="form-control form-control-sm"
            type="text"
            inputMode="url"
            spellCheck="false"
            value={String(node.attributes?.href || '')}
            placeholder="https://example.com or mailto:name@example.com"
            aria-describedby={`simple-link-help-${node.id}`}
            onChange={(event) => onSetAttribute('href', event.target.value)}
          />
          <p className="simple-field-help" id={`simple-link-help-${node.id}`}>
            Use a web address, email address, or page link.
          </p>
          <div className="form-check mb-3">
            <input
              id={`simple-new-tab-${node.id}`}
              className="form-check-input"
              type="checkbox"
              checked={newTab}
              onChange={(event) => {
                if (event.target.checked) {
                  onSetAttribute('target', '_blank');
                  onSetAttribute('rel', 'noopener noreferrer');
                } else {
                  onRemoveAttribute('target');
                  onRemoveAttribute('rel');
                }
              }}
            />
            <label className="form-check-label small" htmlFor={`simple-new-tab-${node.id}`}>Open in a new tab</label>
          </div>
        </>
      ) : null}

      {isImage ? (
        <>
          <label className="form-label small fw-semibold" htmlFor={`simple-image-alt-${node.id}`}>Image description</label>
          <input
            id={`simple-image-alt-${node.id}`}
            className="form-control form-control-sm"
            type="text"
            value={String(node.attributes?.alt || '')}
            aria-describedby={`simple-image-alt-help-${node.id}`}
            onChange={(event) => onSetAttribute('alt', event.target.value)}
          />
          <p className="simple-field-help" id={`simple-image-alt-help-${node.id}`}>
            Describe important image content. Leave blank only when the image is decorative.
          </p>
          <button className="btn btn-primary btn-sm w-100 mb-3" type="button" onClick={onChangeImage}>
            <i className="bi bi-image me-2" aria-hidden="true"></i>Choose another image
          </button>
          <GuidedChoiceGroup
            controlId="image-shape"
            nodeId={node.id}
            label="Image shape"
            options={SIMPLE_APPEARANCE_OPTIONS.imageShape}
            value={getSimpleAppearanceValue(node, 'imageShape') || 'img-fluid'}
            onChange={(value) => onApplyAppearance('imageShape', value)}
          />
          <GuidedChoiceGroup
            controlId="image-size"
            nodeId={node.id}
            label="Image size"
            options={SIMPLE_APPEARANCE_OPTIONS.width}
            value={getSimpleAppearanceValue(node, 'width') || 'w-auto'}
            onChange={(value) => onApplyAppearance('width', value)}
          />
        </>
      ) : null}

      {!isImage ? (
        <GuidedChoiceGroup
          controlId="alignment"
          nodeId={node.id}
          label="Alignment"
          options={SIMPLE_APPEARANCE_OPTIONS.alignment}
          value={getSimpleAppearanceValue(node, 'alignment')}
          onChange={(value) => onApplyAppearance('alignment', value)}
        />
      ) : null}

      {section ? (
        <>
          <GuidedChoiceGroup
            controlId="spacing"
            nodeId={node.id}
            label="Spacing"
            options={SIMPLE_APPEARANCE_OPTIONS.spacing}
            value={getSimpleAppearanceValue(node, 'spacing') || 'py-4'}
            onChange={(value) => onApplyAppearance('spacing', value)}
          />
          <GuidedChoiceGroup
            controlId="colour"
            nodeId={node.id}
            label="Colour"
            options={SIMPLE_APPEARANCE_OPTIONS.tone}
            value={getSimpleAppearanceValue(node, 'tone')}
            onChange={(value) => onApplyAppearance('tone', value)}
          />
          <GuidedChoiceGroup
            controlId="section-width"
            nodeId={node.id}
            label="Section width"
            options={SIMPLE_APPEARANCE_OPTIONS.width}
            value={getSimpleAppearanceValue(node, 'width') || 'w-auto'}
            onChange={(value) => onApplyAppearance('width', value)}
          />
          <fieldset className="mb-3">
            <legend className="form-label small fw-semibold mb-2">Finishing touches</legend>
            <div className="simple-choice-grid">
              <ToggleChoice
                id={`simple-border-${node.id}`}
                label="Border"
                description="Add a visible edge"
                iconClass="bi-border"
                value="border"
                active={classes.includes('border')}
                onChange={(value) => onApplyAppearance('border', value)}
              />
              <ToggleChoice
                id={`simple-rounded-${node.id}`}
                label="Rounded"
                description="Soften the corners"
                iconClass="bi-square"
                value="rounded"
                active={classes.includes('rounded')}
                onChange={(value) => onApplyAppearance('rounded', value)}
              />
              <ToggleChoice
                id={`simple-shadow-${node.id}`}
                label="Shadow"
                description="Lift it from the page"
                iconClass="bi-layers"
                value="shadow"
                active={classes.includes('shadow')}
                onChange={(value) => onApplyAppearance('shadow', value)}
              />
            </div>
          </fieldset>
        </>
      ) : null}
    </div>
  );
};

SimpleElementSettings.propTypes = {
  node: PropTypes.object.isRequired,
  currentText: PropTypes.string.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onSetAttribute: PropTypes.func.isRequired,
  onRemoveAttribute: PropTypes.func.isRequired,
  onChangeImage: PropTypes.func.isRequired,
  onApplyAppearance: PropTypes.func.isRequired,
};

export default SimpleElementSettings;
